import { create } from "zustand";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "mada-theme";

/** Sans préférence enregistrée : clair, pas « système » — un compte tout
 *  juste créé ne doit pas hériter du thème sombre du poste au premier
 *  affichage (cf. index.html, même défaut avant hydratation React). */
function readStored(): ThemePreference {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "light";
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** True si la préférence donnée se résout actuellement en sombre. */
export function resolveDark(pref: ThemePreference): boolean {
  return pref === "dark" || (pref === "system" && systemPrefersDark());
}

/** Applique la classe `.dark` sur <html> selon la préférence. */
export function applyTheme(pref: ThemePreference): void {
  document.documentElement.classList.toggle("dark", resolveDark(pref));
}

interface ThemeState {
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readStored(),
  setPreference: (pref) => {
    localStorage.setItem(STORAGE_KEY, pref);
    applyTheme(pref);
    set({ preference: pref });
  },
}));

/** Suit les changements du thème système quand la préférence est « système ».
 * À appeler une fois au démarrage de l'app. */
export function watchSystemTheme(): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    if (useThemeStore.getState().preference === "system") {
      applyTheme("system");
    }
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
